import { useState } from "react";
import type { ToolCall } from "../lib/api";

/** Human labels for the tools the agents actually call. */
const NICE: Record<string, string> = {
  Bash: "Ran a command",
  PowerShell: "Ran PowerShell",
  Read: "Read a file",
  Write: "Wrote a file",
  Edit: "Edited a file",
  Glob: "Searched for files",
  Grep: "Searched file contents",
  WebFetch: "Fetched a page",
  WebSearch: "Searched the web",
  Task: "Ran a subagent",
  TodoWrite: "Updated its plan",
};

function label(name: string): string {
  return NICE[name] ?? name.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim() ?? "Tool";
}

/** Pull the most useful one-liner out of a tool's input for the collapsed row. */
function summarise(input: unknown): string {
  if (!input || typeof input !== "object") return "";
  const o = input as Record<string, unknown>;
  const first =
    o.command ?? o.file_path ?? o.path ?? o.pattern ?? o.url ?? o.query ?? o.description;
  if (typeof first !== "string") return "";
  return first.replace(/\s+/g, " ").trim().slice(0, 120);
}

export function ToolCard({ tool }: { tool: ToolCall }) {
  const [open, setOpen] = useState(false);
  const detail = summarise(tool.input);
  const hasBody = Boolean(tool.output) || tool.input !== undefined;

  return (
    <div className={`tool tool--${tool.status}`}>
      <button
        type="button"
        className="tool__head"
        onClick={() => hasBody && setOpen((v) => !v)}
        aria-expanded={open}
        disabled={!hasBody}
      >
        <span className="tool__icon" aria-hidden="true">
          {tool.status === "running" ? (
            <span className="spinner" />
          ) : (
            <span
              className={`dot ${tool.status === "ok" ? "dot--ok" : "dot--fault"}`}
            />
          )}
        </span>
        <span className="tool__label">{label(tool.name)}</span>
        {detail && <span className="tool__detail mono truncate">{detail}</span>}
        {hasBody && <span className={`tool__chev ${open ? "is-open" : ""}`} aria-hidden="true">›</span>}
      </button>

      {open && (
        <div className="tool__body">
          {tool.input !== undefined && (
            <>
              <div className="tool__section">Input</div>
              <pre className="tool__pre scroll">
                {typeof tool.input === "string"
                  ? tool.input
                  : JSON.stringify(tool.input, null, 2)}
              </pre>
            </>
          )}
          {tool.output && (
            <>
              <div className="tool__section">Output</div>
              <pre className="tool__pre scroll">{tool.output}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
